import React, { useState, useEffect } from 'react';
import {
	Grid,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	SelectChangeEvent,
} from '@mui/material';
import { IOnboardingSettings } from 'core/interfaces/onboardingInterface';
import { createOnboardingSettings } from 'core/API/onboarding';
import { getPartners } from 'core/API/partners';
import { Partner } from 'types';
import styles from '../SchedulerSetting.module.scss';

interface EditGeneralSettingsProps {
	settings: IOnboardingSettings | null;
	handleBackClick?: () => void;
	onSettingsUpdated?: () => void;
}

const EditGeneralSettings: React.FC<EditGeneralSettingsProps> = ({
	settings,
	handleBackClick,
	onSettingsUpdated,
}) => {
	const [settingsBuffer, setSettingsBuffer] = useState(() => {
		const getWorkplace = () => {
			if (settings?.workplace) {
				return settings.workplace;
			}
			const workplaceId = localStorage.getItem('workplace-id');
			return workplaceId ? `/api/workplaces/${workplaceId}` : '';
		};

		return {
			workplace: getWorkplace(),
			ein: settings?.ein || '',
			storageProvider: settings?.storageProvider || ('google' as 'google' | 'onedrive'),
		};
	});

	const [partners, setPartners] = useState<Partner[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoadingPartners, setIsLoadingPartners] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		loadPartners();
	}, []);

	const loadPartners = async () => {
		try {
			setIsLoadingPartners(true);
			const partnersData = await getPartners({ active: true });

			// Filter for Google Drive and OneDrive partners
			const storagePartners = partnersData.filter(
				(p) =>
					p.label?.toLowerCase().includes('google') ||
					p.label?.toLowerCase().includes('drive') ||
					p.label?.toLowerCase().includes('onedrive') ||
					p.label?.toLowerCase().includes('microsoft')
			);

			setPartners(storagePartners);
		} catch (err) {
			console.error('Error loading partners:', err);
		} finally {
			setIsLoadingPartners(false);
		}
	};

	const getFilteredPartners = (): Partner[] => {
		if (settingsBuffer.storageProvider === 'google') {
			return partners.filter(
				(p) => p.label?.toLowerCase().includes('google') || p.label?.toLowerCase().includes('drive')
			);
		} else {
			return partners.filter(
				(p) =>
					p.label?.toLowerCase().includes('ms one drive') ||
					p.label?.toLowerCase().includes('ms onedrive') ||
          p.label?.toLowerCase().includes('microsoft')
			);
		}
	};

	const validateEIN = (ein: string): boolean => {
		// EIN format: XX-XXXXXXX (2 digits, hyphen, 7 digits)
		const einRegex = /^\d{2}-\d{7}$/;
		return einRegex.test(ein);
	};

	const validateSettings = (): boolean => {
		// Validate EIN
		if (!settingsBuffer.ein.trim()) {
			setError('EIN is required');
			return false;
		}

		if (!validateEIN(settingsBuffer.ein)) {
			setError('Invalid EIN format. Expected format: XX-XXXXXXX (e.g., 12-3456789)');
			return false;
		}

		// Validate storage provider
		if (!settingsBuffer.storageProvider) {
			setError('Storage provider is required');
			return false;
		}

		const filteredPartners = getFilteredPartners();
		if (filteredPartners.length === 0) {
			setError(
				`No ${settingsBuffer.storageProvider === 'google' ? 'Google Drive' : 'OneDrive'} partner configured. Please configure partners first.`
			);
			return false;
		}

		if (!settingsBuffer.workplace) {
			setError('Workplace is required');
			return false;
		}

		return true;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!validateSettings()) {
			return;
		}

		try {
			setIsSubmitting(true);
			// Always use POST API as per requirement
			await createOnboardingSettings({
				ein: settingsBuffer.ein,
				storageProvider: settingsBuffer.storageProvider,
				workplace: settingsBuffer.workplace,
			});

			onSettingsUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err: any) {
			setError(err?.message || 'Failed to update settings');
			setIsSubmitting(false);
		}
	};

	const handleStorageProviderChange = (event: SelectChangeEvent<'google' | 'onedrive'>) => {
		const value = event.target.value as 'google' | 'onedrive';
		setSettingsBuffer((prev) => ({
			...prev,
			storageProvider: value,
		}));
	};

	return (
		<div className={styles.editGeneralSettings}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick} variant="contained">
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting}
						variant="contained"
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit General Settings</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Employer Identification Number (EIN)"
					name="ein"
					value={settingsBuffer.ein}
					onChange={(e) => setSettingsBuffer({ ...settingsBuffer, ein: e.target.value })}
					placeholder="XX-XXXXXXX"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					helperText="Format: XX-XXXXXXX (e.g., 12-3456789)"
					inputProps={{
						maxLength: 10,
					}}
				/>

				<FormControl fullWidth margin="normal" required className={styles.input}>
					<InputLabel>Storage Provider</InputLabel>
					<Select
						value={settingsBuffer.storageProvider}
						onChange={handleStorageProviderChange}
						label="Storage Provider"
						disabled={isLoadingPartners || isSubmitting}
					>
						<MenuItem value="google">Google Drive</MenuItem>
						<MenuItem value="onedrive">Microsoft OneDrive</MenuItem>
					</Select>
				</FormControl>

				{!isLoadingPartners && getFilteredPartners().length === 0 && (
					<div style={{ color: '#ed6c02', marginTop: 8, fontSize: '0.875rem' }}>
						No {settingsBuffer.storageProvider === 'google' ? 'Google Drive' : 'OneDrive'} partner
						configured. Please configure in Partners section first.
					</div>
				)}
			</form>
		</div>
	);
};

export default EditGeneralSettings;
